import { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Box,
  Checkbox,
  UnorderedList,
  ListItem,
  useColorModeValue,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@chakra-ui/react';
import { WarningIcon } from '@chakra-ui/icons';

import { CONSENT_METHOD_VERBAL, CONSENT_METHOD_WRITTEN } from '../constants';

export interface DeclinedItem {
  key: string;
  name: string;
}

interface DeclineAttestationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (consentMethod: number) => void;
  declinedItems: DeclinedItem[];
  attestationText: string;
  isLoading?: boolean;
  size?: 'md' | 'lg';
  secondaryItems?: DeclinedItem[];
  secondaryTitle?: string;
}

const DeclineAttestationModal = ({
  isOpen,
  onClose,
  onConfirm,
  declinedItems,
  attestationText,
  isLoading = false,
  size = 'md',
  secondaryItems,
  secondaryTitle,
}: DeclineAttestationModalProps) => {
  const warningBg = useColorModeValue('orange.50', 'orange.900');
  const warningBorder = useColorModeValue('orange.200', 'orange.700');

  const [isChecked, setIsChecked] = useState(false);
  const [consentMethod, setConsentMethod] = useState<string>(
    String(CONSENT_METHOD_VERBAL),
  );

  useEffect(() => {
    if (isOpen) {
      setIsChecked(false);
      setConsentMethod(String(CONSENT_METHOD_VERBAL));
    }
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    onConfirm(parseInt(consentMethod, 10));
  }, [onConfirm, consentMethod]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const hasDeclinedItems =
    declinedItems.length > 0 || (secondaryItems?.length ?? 0) > 0;

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} isCentered size={size}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Box display="flex" alignItems="center" gap={2}>
            <WarningIcon color="orange.500" />
            <Text>Coverage Decline Confirmation</Text>
          </Box>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Box
              p={4}
              bg={warningBg}
              borderRadius="md"
              borderWidth="1px"
              borderColor={warningBorder}
            >
              <Text fontSize="sm" fontWeight="600" mb={2}>
                The following coverages are recommended by Billyard Insurance
                Group and have been declined:
              </Text>

              {declinedItems.length > 0 && (
                <UnorderedList spacing={1} pl={2} mb={2}>
                  {declinedItems.map((item) => (
                    <ListItem key={item.key} fontSize="sm">
                      {item.name}
                    </ListItem>
                  ))}
                </UnorderedList>
              )}

              {secondaryItems && secondaryItems.length > 0 && (
                <>
                  <Text fontSize="sm" fontWeight="600" mt={3} mb={1}>
                    {secondaryTitle || 'Additional Declined Items:'}
                  </Text>
                  <UnorderedList spacing={1} pl={2}>
                    {secondaryItems.map((item) => (
                      <ListItem key={item.key} fontSize="sm">
                        {item.name}
                      </ListItem>
                    ))}
                  </UnorderedList>
                </>
              )}
            </Box>

            <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
              Please confirm the following:
            </Text>

            <Checkbox
              isChecked={isChecked}
              onChange={(e) => setIsChecked(e.target.checked)}
              colorScheme="blue"
              alignItems="flex-start"
            >
              <Text fontSize="sm" mt="-2px">
                {attestationText}
              </Text>
            </Checkbox>

            <FormControl>
              <FormLabel fontSize="sm" fontWeight="600">
                Consent Method
              </FormLabel>
              <RadioGroup value={consentMethod} onChange={setConsentMethod}>
                <VStack align="start" spacing={2}>
                  <Radio
                    value={String(CONSENT_METHOD_VERBAL)}
                    colorScheme="blue"
                  >
                    Verbal Consent
                  </Radio>
                  <Radio
                    value={String(CONSENT_METHOD_WRITTEN)}
                    colorScheme="blue"
                  >
                    Written Consent
                  </Radio>
                </VStack>
              </RadioGroup>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={handleCancel} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="brand"
            onClick={handleConfirm}
            isDisabled={!isChecked || !hasDeclinedItems}
            isLoading={isLoading}
          >
            Apply
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeclineAttestationModal;
